import {
  CsvExportParams,
  ExcelExportParams,
  GridApi,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import { useMemo } from 'react';

interface IProps {
  gridApi: GridApi;
  excelExportParams?: Partial<ExcelExportParams>;
}

export const useAgGridExcelExport = ({ gridApi, excelExportParams = {} }: IProps) => {
  const exportableColIds = useMemo(() => {
    if (!gridApi) {
      return [];
    }

    // `getAllDisplayedColumns`, not `getColumns`: the latter returns every column "regardless of
    // visible or not", and naming a hidden column in `columnKeys` forces it back into the export —
    // so a user who hid columns via a saved layout would get them all back.
    // Action columns (`context.nonExportable`) carry no field or valueGetter, so exporting them
    // emits a labelled but permanently blank spreadsheet column.
    return gridApi
      .getAllDisplayedColumns()
      .filter((col) => !col.getColDef().context?.nonExportable)
      .map((col) => col.getColId())
      .filter((colId) => !colId.startsWith('ag-'));
  }, [gridApi]);

  const defaultProcessArrayCell = (value: unknown[]): string => {
    if (value.length === 0) {
      return '';
    }

    // If array contains strings, export only non empty strings
    if (typeof value[0] === 'string') {
      return value.filter((x) => typeof x === 'string' && x.length > 0).join(', ');
    }

    // For other object types, try to stringify them
    return value.map((item) => String(item)).join(', ');
  };

  const defaultExcelExportParams: ExcelExportParams = useMemo(() => {
    const { processCellCallback, ...rest } = excelExportParams;

    const defaultProcessCellCallback = (
      params: ProcessCellForExportParams,
      processCallback: (params: ProcessCellForExportParams) => string
    ) => {
      const { value, formatValue } = params;

      let formattedValue = formatValue(value);

      // If value is an array and not formatted correctly (not given a custom valueFormatter)
      if (Array.isArray(value) && formattedValue === value?.toString()) {
        formattedValue = defaultProcessArrayCell(value);
      }

      if (processCallback) {
        const processedValue = processCallback(params);

        formattedValue = formatValue(processedValue) ?? processedValue;
      }

      return formattedValue;
    };

    return {
      columnKeys: exportableColIds,
      processCellCallback: (params: ProcessCellForExportParams) =>
        defaultProcessCellCallback(params, processCellCallback),
      ...rest,
    };
  }, [excelExportParams, exportableColIds]);

  // CSV export takes no other defaults, so it only needs the same column selection.
  const defaultCsvExportParams: CsvExportParams = useMemo(
    () => ({ columnKeys: exportableColIds }),
    [exportableColIds]
  );

  return {
    defaultCsvExportParams,
    defaultExcelExportParams,
  };
};
